import Button from '@rio-cloud/rio-uikit/Button';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FormattedMessage } from 'react-intl';
import { useDeleteAppLoginMutation, useGetAppLoginsQuery } from '../../services/appLoginApi';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { changeRowSelection, getSelectedAppLoginId } from './sidebarSlice';
import type { AppLogin } from '../../services/appLoginCodec';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import type React from 'react';
import { useEffect, useState } from 'react';
import Notification from '@rio-cloud/rio-uikit/Notification';

const SidebarDeleteDialogBody = () => {
    return (
        <div data-testid={'sidebar-delete-dialog-body'}>
            <FormattedMessage id={'intl-msg.sidebar.deleteDialog.content'} />
        </div>
    );
};

const SidebarFooter = ({
    setShowDeleteDialog,
}: {
    setShowDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    return (
        <div className='btn-toolbar pull-left'>
            <Button
                autoTrackingKey='intl-msg:button.delete'
                bsStyle={Button.DANGER}
                variant={Button.VARIANT_LINK}
                disabled={false}
                onClick={() => setShowDeleteDialog(true)}
                data-testid='detailViewDeleteButton'
            >
                <span className='rioglyph rioglyph-trash text-color-danger margin-right-5' />
                <span>
                    <FormattedMessage id={'intl-msg:button.delete'} />
                </span>
            </Button>
        </div>
    );
};

export const AppLoginsSidebar = () => {
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const { data } = useGetAppLoginsQuery();
    const [deleteAppLogin, { isSuccess, isError }] = useDeleteAppLoginMutation();
    const dispatch = useAppDispatch();
    const selectedRow = useAppSelector(getSelectedAppLoginId);

    const handleRowSelectionChange = () => {
        dispatch(changeRowSelection(null));
    };

    useEffect(() => {
        if (isError) {
            Notification.error(<FormattedMessage id='intl-msg:appLogin.notification.single-deletion.error' />);
        }
        if (isSuccess) {
            Notification.success(<FormattedMessage id='intl-msg:appLogin.notification.single-deletion.success' />);
        }
    }, [isError, isSuccess]);

    const getAppLoginDescription = (appLoginId: string): string | null => {
        if (!data) {
            return null;
        }

        const fistMatchingAppLogin = data.find((appLogin: AppLogin) => appLogin.appLoginId === appLoginId);
        return fistMatchingAppLogin?.description ?? null;
    };

    const handleConfirmDeletion = () => {
        if (selectedRow) {
            deleteAppLogin({ appLoginId: selectedRow });
        }
        setShowDeleteDialog(false);
        dispatch(changeRowSelection(null));
    };

    return (
        <>
            <Sidebar
                title={<div data-testid='sidebar-title'>{selectedRow && getAppLoginDescription(selectedRow)}</div>}
                titleClassName='padding-left-10'
                closed={false}
                onClose={handleRowSelectionChange}
                position={Sidebar.RIGHT}
                resizable
                minWidth={300}
                maxWidth={600}
                width={420}
                switchModeBreakpoint={1400}
                footer={<SidebarFooter setShowDeleteDialog={setShowDeleteDialog} />}
            >
                <div className='padding-left-20 padding-right-20' data-testid={'app-login-sidebar'}>
                    QR-Code may be added here
                </div>
            </Sidebar>
            <ConfirmationDialog
                show={showDeleteDialog}
                title={<FormattedMessage id={'intl-msg:sidebar.deleteDialog.title'} />}
                bsSize={'sm'}
                content={<SidebarDeleteDialogBody />}
                onClickConfirm={handleConfirmDeletion}
                onClickCancel={() => setShowDeleteDialog(false)}
                cancelButtonAutoTrackingKey='intl-msg:button.cancel'
                confirmButtonAutoTrackingKey='intl-msg:button.confirm'
                cancelButtonText={<FormattedMessage id={'intl-msg:button.cancel'} />}
                confirmButtonText={<FormattedMessage id={'intl-msg:button.confirm'} />}
                useOverflow
                disableConfirm={false}
            />
        </>
    );
};
